// Client-side geo helpers for Brivio provider discovery (option 2a).
// Geocoding uses the Google Maps JS Geocoder (loaded by the map). ZIP → centroid
// is coarse by design (service-area level), so no exact address is ever exposed.

export const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Explicit kill-switch: set REACT_APP_ENABLE_GOOGLE_MAPS="false" to force the
// stylized fallback (e.g. if the key is unconfigured/restricted in production).
const MAPS_FLAG = process.env.REACT_APP_ENABLE_GOOGLE_MAPS;
export const MAPS_ENABLED =
  MAPS_FLAG === "false"
    ? false
    : !!MAPS_KEY && MAPS_KEY !== "YOUR_GOOGLE_MAPS_API_KEY";

const GEO_CACHE_KEY = "brivio_zip_geo_v1";
const AREA_KEY = "brivio_search_area_v1";

export function loadZipCache() {
  try { return JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || "{}"); } catch { return {}; }
}
function saveZipCache(cache) {
  try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache)); } catch { /* ignore */ }
}

// Geocode a single ZIP (US) to {lat,lng} using the JS Geocoder, with localStorage cache.
export function geocodeZip(zip) {
  return new Promise((resolve) => {
    const z = String(zip || "").trim().slice(0, 5);
    if (!z || !window.google?.maps) return resolve(null);
    const cache = loadZipCache();
    if (cache[z]) return resolve(cache[z]);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: z, componentRestrictions: { country: "US" } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const loc = results[0].geometry.location;
        const point = { lat: loc.lat(), lng: loc.lng() };
        cache[z] = point;
        saveZipCache(cache);
        resolve(point);
      } else {
        resolve(null);
      }
    });
  });
}

// Geocode a free-text place (for "Save my search area").
export function geocodePlace(query) {
  return new Promise((resolve) => {
    if (!query || !window.google?.maps) return resolve(null);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query, componentRestrictions: { country: "US" } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng(), label: results[0].formatted_address });
      } else resolve(null);
    });
  });
}

export function getBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  });
}

// Haversine distance in miles.
export function distanceMiles(a, b) {
  if (!a || !b) return null;
  const R = 3958.8;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function loadSavedArea() {
  try { return JSON.parse(localStorage.getItem(AREA_KEY) || "null"); } catch { return null; }
}
export function saveSearchArea(area) {
  try { localStorage.setItem(AREA_KEY, JSON.stringify(area)); } catch { /* ignore */ }
}
export function clearSavedArea() {
  try { localStorage.removeItem(AREA_KEY); } catch { /* ignore */ }
}

// Privacy-safe ZIP -> city label for Brivio's Illinois-first beta.
// Mirrors backend geo_labels.py (town-level only, never exact addresses).
const ZIP_CITY = {
  "60544": "Plainfield", "60585": "Plainfield", "60586": "Plainfield",
  "60431": "Joliet", "60432": "Joliet", "60433": "Joliet",
  "60434": "Joliet", "60435": "Joliet", "60436": "Joliet",
  "60404": "Shorewood", "60403": "Crest Hill",
  "60440": "Bolingbrook", "60490": "Bolingbrook",
  "60446": "Romeoville", "60441": "Lockport", "60442": "Manhattan",
  "60540": "Naperville", "60563": "Naperville", "60564": "Naperville", "60565": "Naperville",
  "60543": "Oswego", "60538": "Montgomery", "60560": "Yorkville",
  "60502": "Aurora", "60503": "Aurora", "60504": "Aurora", "60505": "Aurora", "60506": "Aurora",
  "60451": "New Lenox", "60423": "Frankfort", "60448": "Mokena",
  "60187": "Wheaton", "60189": "Wheaton", "60515": "Downers Grove",
  "60516": "Downers Grove", "60532": "Lisle",
};

export const isIllinoisZip = (zip) => {
  const z = String(zip || "").trim().slice(0, 5);
  if (!/^\d{5}$/.test(z)) return false;
  const p = parseInt(z.slice(0, 3), 10);
  return p >= 600 && p <= 629;
};

// 'City, IL' for a known Brivio-area ZIP, 'Illinois' for other IL ZIPs, or null.
export const cityStateFromZip = (zip) => {
  const z = String(zip || "").trim().slice(0, 5);
  if (!z) return null;
  if (ZIP_CITY[z]) return `${ZIP_CITY[z]}, IL`;
  if (isIllinoisZip(z)) return "Illinois";
  return null;
};
